/**
 * The page's language, as the rest of the app needs it.
 *
 * Three small things that all answer to the URL, kept together because they
 * are the same decision seen from three places: the router redirects by it,
 * Apollo states it to the API, and the i18n provider renders by it.
 */
import { DEFAULT_LANGUAGE, isLanguage, LANGUAGES, type Language } from '@athena/i18n'

/**
 * The language a path is prefixed with, or null.
 *
 * Used where there is no route match to read `params` from: the not-found
 * screen, and Apollo's first request before the router has settled. The router
 * is the authority everywhere else — see `router.tsx`.
 */
export function languageFromPathname(pathname: string): Language | null {
  const first = pathname.split('/')[1]
  return isLanguage(first) ? first : null
}

/**
 * The same path under a different language.
 *
 * Not a string replace of the tag. `/es/practices/es` and a practice titled
 * `es` are both real, and `pathname.replace('/es', '/en')` gets the second one
 * wrong the moment a route parameter happens to spell a language. Only the
 * first segment is a language, so only the first segment is rewritten.
 *
 * A path with no language prefix gains one rather than losing its first
 * segment, which is what makes this safe to call on any location.
 */
export function withLanguage(pathname: string, language: Language): string {
  const segments = pathname.split('/')
  // ['', 'es', 'practices'] — index 1 is the prefix, present or not.
  if (isLanguage(segments[1])) {
    segments[1] = language
    return segments.join('/')
  }
  return `/${language}${pathname === '/' ? '' : pathname}`
}

/**
 * What Apollo puts in `accept-language`, and what the i18n provider renders in.
 *
 * A module-level value rather than context, because the one reader that cannot
 * use context is Apollo's link: it is built once, outside React, and runs for
 * every request. The language route sets this as it renders, above every
 * component that can start a query, so a request always carries the language
 * of the page that made it (V1.md section 5).
 *
 * Spanish until the router has matched anything, which covers the very first
 * `me` query — the one the redirect logic itself makes, before there is a page
 * with a language at all.
 */
let current: Language = DEFAULT_LANGUAGE

export function setCurrentLanguage(language: Language): void {
  current = language
}

export function currentLanguage(): Language {
  return current
}

export { DEFAULT_LANGUAGE, isLanguage, LANGUAGES, type Language }
